import { QRCodeSVG } from "qrcode.react";
import { FC } from "react";
import { useTranslation } from "react-i18next";

import { getEsimQrValue } from "@/utils/esimActivationCode";

interface Props {
  /** The SIM `matchingId` as delivered by the API (may be missing/malformed). */
  matchingId: string | undefined;
  /** Edge length of the rendered QR code in pixels. */
  size: number;
}

/**
 * Renders the eSIM activation QR code for a `matchingId`, or the shared
 * "invalid code" hint when the code is missing or malformed.
 *
 * A QR built from an unparseable code can't be installed by any device, so we
 * show the same message RegistrationDetails uses on its error path instead of
 * a dead QR the user would scan in vain.
 */
export const EsimQrCode: FC<Props> = ({ matchingId, size }) => {
  const { t } = useTranslation();
  const qrValue = getEsimQrValue(matchingId);

  if (!qrValue) {
    return (
      <p
        data-testid="esim-qr-invalid-code"
        className="text-body-xs text-foreground-secondary"
      >
        {t(
          "cockpit.managementTile.details.sections.details.registrationDetails.invalidCode",
        )}
      </p>
    );
  }

  return (
    <QRCodeSVG
      value={qrValue}
      size={size}
      role="img"
      aria-label={t(
        "portal:cockpit.managementTile.details.sections.details.scanQRCode",
      )}
    />
  );
};
